import { useCallback, useEffect, useRef, useState } from 'react';
import { runAppDataFetch, DEFAULT_APP_FETCH_MS } from '../../lib/appDataFetch.js';
import { useAppSession } from '../../lib/appSessionBridge.js';

/**
 * Carga de datos estándar en toda la app: espera `authReady`, timeout, reconexión y reintento.
 *
 * @template T
 * @param {() => Promise<T>} fetcher
 * @param {unknown[]} [deps] — dependencias del fetcher (p. ej. userId, filtros)
 * @param {{ enabled?: boolean, timeoutMs?: number, timeoutMessage?: string, initialLoading?: boolean }} [opts]
 */
export function useAppDataLoad(fetcher, deps = [], opts = {}) {
  const { authReady, dataRefreshKey } = useAppSession();
  const { enabled = true, timeoutMs = DEFAULT_APP_FETCH_MS, timeoutMessage, initialLoading = true } = opts;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled && initialLoading);
  const reqIdRef = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }
    if (!authReady) return;

    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await runAppDataFetch(fetcher, { timeoutMs, timeoutMessage });
      if (reqId !== reqIdRef.current) return;
      setData(result);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setError(e?.message || 'No se pudieron cargar los datos.');
      setData(null);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps explícitas del caller
  }, [enabled, authReady, fetcher, timeoutMs, timeoutMessage, ...deps]);

  useEffect(() => {
    reload();
  }, [reload, dataRefreshKey]);

  const waitingAuth = enabled && !authReady;

  return {
    data,
    error,
    loading: waitingAuth || loading,
    reload,
    authReady,
  };
}
